import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { getDeliveries, getDeliveryDetail } from "../../api/deliveryCompanyService";
import { DeliveryStatusColors, DeliveryStatusLabels, formatCurrency, formatDate } from "../Admin/constants";
import OrderLabelDialog from "./OrderLabelDialog";

// Maps each date filter to its dcShared:periods label key.
const DATE_FILTERS = {
  today: "today",
  yesterday: "yesterday",
  week: "pastWeek",
  all: "allTime",
};

function getDateRange(filter) {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  switch (filter) {
    case "today": {
      const to = new Date(startOfToday);
      to.setDate(to.getDate() + 1);
      return { fromDate: startOfToday, toDate: to };
    }
    case "yesterday": {
      const from = new Date(startOfToday);
      from.setDate(from.getDate() - 1);
      return { fromDate: from, toDate: startOfToday };
    }
    case "week": {
      const from = new Date(startOfToday);
      from.setDate(from.getDate() - 6);
      const to = new Date(startOfToday);
      to.setDate(to.getDate() + 1);
      return { fromDate: from, toDate: to };
    }
    default:
      return { fromDate: null, toDate: null };
  }
}

export default function Orders() {
  const { t } = useTranslation(["dcOrders", "dcShared"]);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [orderNumberInput, setOrderNumberInput] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [selectedDeliveryId, setSelectedDeliveryId] = useState(null);

  useEffect(() => {
    const handle = setTimeout(() => {
      setOrderNumber(orderNumberInput.trim());
      setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, 400);
    return () => clearTimeout(handle);
  }, [orderNumberInput]);

  useEffect(() => {
    const { fromDate, toDate } = getDateRange(dateFilter);
    setLoading(true);
    getDeliveries({
      status: statusFilter || undefined,
      orderNumber: orderNumber || undefined,
      fromDate: fromDate ? fromDate.toISOString() : undefined,
      toDate: toDate ? toDate.toISOString() : undefined,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    })
      .then((data) => {
        setRows(data.items);
        setRowCount(data.totalCount);
        setError("");
      })
      .catch(() => setError("dcOrders:loadError"))
      .finally(() => setLoading(false));
  }, [statusFilter, orderNumber, dateFilter, paginationModel]);

  const handleDateFilterChange = (event, value) => {
    if (value === null) return;
    setDateFilter(value);
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  };

  const columns = useMemo(
    () => [
      { field: "orderNumber", headerName: t("dcShared:orderNumber"), width: 260 },
      { field: "storeName", headerName: t("dcShared:store"), flex: 1, minWidth: 150 },
      { field: "customerName", headerName: t("dcShared:customer"), flex: 1, minWidth: 150 },
      {
        field: "pickupCity",
        headerName: t("dcShared:from"),
        width: 170,
        valueGetter: (v, row) => row.pickupCity || row.pickupAddressText || "-",
      },
      {
        field: "dropoffCity",
        headerName: t("dcShared:to"),
        width: 170,
        valueGetter: (v, row) => `${row.dropoffCity}, ${row.dropoffStreet}`,
      },
      { field: "driverName", headerName: t("dcShared:driver"), width: 150, valueGetter: (v) => v || t("dcShared:unassigned") },
      {
        field: "status",
        headerName: t("dcShared:status"),
        width: 160,
        renderCell: (params) => (
          <Chip size="small" label={t(`dcShared:deliveryStatus.${params.value}`)} color={DeliveryStatusColors[params.value]} />
        ),
      },
      {
        field: "orderPrice",
        headerName: t("dcOrders:orderPrice"),
        width: 130,
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "deliveryFee",
        headerName: t("dcShared:deliveryFee"),
        width: 130,
        valueFormatter: (v) => formatCurrency(v),
      },
      { field: "orderCreatedAt", headerName: t("dcShared:ordered"), width: 170, valueFormatter: (v) => formatDate(v) },
    ],
    [t]
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {t("dcOrders:title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t(error)}
        </Alert>
      )}

      <ToggleButtonGroup
        value={dateFilter}
        exclusive
        onChange={handleDateFilterChange}
        size="small"
        sx={{ mb: 2 }}
      >
        {Object.entries(DATE_FILTERS).map(([value, labelKey]) => (
          <ToggleButton key={value} value={value}>
            {t(`dcShared:periods.${labelKey}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <TextField
          size="small"
          label={t("dcOrders:searchByOrder")}
          value={orderNumberInput}
          onChange={(e) => setOrderNumberInput(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t("dcShared:status")}</InputLabel>
          <Select label={t("dcShared:status")} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">{t("dcOrders:allStatuses")}</MenuItem>
            {Object.keys(DeliveryStatusLabels).map((value) => (
              <MenuItem key={value} value={value}>
                {t(`dcShared:deliveryStatus.${value}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.deliveryId}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          onRowClick={(params) => setSelectedDeliveryId(params.row.deliveryId)}
          sx={{ border: "none", "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      </Box>

      <OrderDetailDialog deliveryId={selectedDeliveryId} onClose={() => setSelectedDeliveryId(null)} />
    </Box>
  );
}

function OrderDetailDialog({ deliveryId, onClose }) {
  const { t } = useTranslation(["dcOrders", "dcShared"]);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [showLabel, setShowLabel] = useState(false);

  useEffect(() => {
    if (!deliveryId) {
      setOrder(null);
      setError("");
      return;
    }
    let active = true;
    getDeliveryDetail(deliveryId)
      .then((data) => {
        if (active) setOrder(data);
      })
      .catch(() => {
        if (active) setError("dcOrders:loadOrderError");
      });
    return () => {
      active = false;
    };
  }, [deliveryId]);

  const open = Boolean(deliveryId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {order ? order.orderNumber : t("dcOrders:orderDetails")}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {order && (
            <IconButton onClick={() => setShowLabel(true)} size="small" title={t("dcOrders:orderLabel")}>
              <QrCode2Icon fontSize="small" />
            </IconButton>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{t(error)}</Alert>}
        {!error && !order && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {order && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {t("dcOrders:items")}
                  </Typography>
                  <List disablePadding>
                    {order.items.map((item, idx) => (
                      <ListItem key={idx} disableGutters divider={idx < order.items.length - 1}>
                        <ListItemAvatar>
                          <Avatar src={item.productImageUrl || undefined} variant="rounded" />
                        </ListItemAvatar>
                        <ListItemText
                          primary={item.productName}
                          secondary={
                            item.variantName
                              ? `${item.variantName} • ${t("dcOrders:qty", { count: item.quantity })}`
                              : t("dcOrders:qty", { count: item.quantity })
                          }
                        />
                        <Typography fontWeight={600}>{formatCurrency(item.unitPrice * item.quantity)}</Typography>
                      </ListItem>
                    ))}
                  </List>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack spacing={0.75}>
                    <SummaryRow label={t("dcOrders:subtotal")} value={formatCurrency(order.subTotal)} />
                    <SummaryRow label={t("dcShared:deliveryFee")} value={formatCurrency(order.deliveryFee)} />
                    <SummaryRow label={t("dcOrders:discount")} value={`- ${formatCurrency(order.discountAmount)}`} />
                    <SummaryRow label={t("dcShared:total")} value={formatCurrency(order.totalAmount)} bold />
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {t("dcOrders:statusHistory")}
                  </Typography>
                  <Stepper orientation="vertical">
                    {order.statusHistory.map((h, idx) => (
                      <Step key={idx} active completed>
                        <StepLabel>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" fontWeight={600}>
                              {t(`dcShared:orderStatus.${h.status}`)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(h.changedAt)}
                            </Typography>
                          </Stack>
                          {h.note && (
                            <Typography variant="caption" color="text.secondary">
                              {h.note}
                            </Typography>
                          )}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Paper>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {t("dcOrders:orderStatus")}
                  </Typography>
                  <Chip label={t(`dcShared:orderStatus.${order.orderStatus}`)} size="small" sx={{ mb: 1 }} />
                  <DetailRow label={t("dcShared:ordered")} value={formatDate(order.orderCreatedAt)} />
                  {order.notes && <DetailRow label={t("dcShared:notes")} value={order.notes} />}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {t("dcShared:customer")}
                  </Typography>
                  <Stack spacing={0.75}>
                    <DetailRow label={t("dcShared:name")} value={order.customerName} />
                    <DetailRow label={t("dcShared:phone")} value={order.customerPhone || "-"} />
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {t("dcOrders:pickupStore")}
                  </Typography>
                  <Stack spacing={0.75}>
                    <DetailRow label={t("dcShared:store")} value={order.storeName} />
                    <DetailRow label={t("dcShared:address")} value={order.pickupCity || order.pickupAddressText || "-"} />
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {t("dcOrders:dropoffCustomer")}
                  </Typography>
                  <Typography variant="body2">
                    {order.dropoffStreet} {order.dropoffBuildingNumber}
                    {order.dropoffApartmentNumber ? `, ${t("dcOrders:apt", { number: order.dropoffApartmentNumber })}` : ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {order.dropoffCity}
                  </Typography>
                  {order.dropoffAdditionalDetails && (
                    <Typography variant="caption" color="text.secondary">
                      {order.dropoffAdditionalDetails}
                    </Typography>
                  )}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {t("dcOrders:delivery")}
                  </Typography>
                  <Stack spacing={0.75}>
                    <Box>
                      <Chip
                        size="small"
                        label={t(`dcShared:deliveryStatus.${order.status}`)}
                        color={DeliveryStatusColors[order.status]}
                      />
                    </Box>
                    <DetailRow label={t("dcShared:driver")} value={order.driverName || t("dcOrders:notYetAssigned")} />
                    <DetailRow label={t("dcOrders:assigned")} value={formatDate(order.assignedAt)} />
                    <DetailRow label={t("dcOrders:pickedUp")} value={formatDate(order.pickedUpAt)} />
                    <DetailRow label={t("dcOrders:delivered")} value={formatDate(order.deliveredAt)} />
                  </Stack>
                </Paper>
              </Stack>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      {showLabel && <OrderLabelDialog order={order} onClose={() => setShowLabel(false)} />}
    </Dialog>
  );
}

function SummaryRow({ label, value, bold }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color={bold ? "text.primary" : "text.secondary"} fontWeight={bold ? 700 : 400}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={bold ? 700 : 400}>
        {value}
      </Typography>
    </Stack>
  );
}

function DetailRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value}
      </Typography>
    </Stack>
  );
}
